/**
 * Hello Retail Alternatives
 *
 * Fetches alternative product recommendations from Hello Retail,
 * renders them via the Section Rendering API, and injects the HTML
 * into the <hello-retail-alternatives> custom element.
 */
(function () {
  if (customElements.get('hello-retail-alternatives')) return;

  class HelloRetailAlternatives extends HTMLElement {
    constructor() {
      super();
      this.initialized = false;
    }

    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      // Run init directly — no lazy loading because the section starts
      // hidden (display:none) which prevents IntersectionObserver from firing.
      this.init();
    }

    async init() {
      const productId = this.dataset.productId;
      const hrKey = this.dataset.hrKey;
      const sectionId = this.dataset.sectionId;
      const limit = parseInt(this.dataset.limit, 10) || 8;

      if (!productId || !hrKey) {
        console.warn('HelloRetailAlternatives: Missing productId or hrKey');
        return;
      }

      // Wait for HelloRetailService
      if (!window.HelloRetailService) {
        const available = await this.waitForService();
        if (!available) {
          console.warn('HelloRetailAlternatives: HelloRetailService not available after retries');
          return;
        }
      }

      try {
        const result = await window.HelloRetailService.fetchRecommendations({
          awsKey: hrKey,
          productIds: [productId],
          count: limit,
          filters: { inStock: 'true' },
          sourceType: 'ALTERNATIVES',
        });

        const products = result?.products;
        const trackingKey = result?.trackingKey || null;

        if (!products || products.length === 0) {
          return;
        }

        // Build search query from returned product IDs
        const query = products
          .map((p) => `id:${p.productNumber || p.id}`)
          .join(' OR ');

        // Fetch rendered HTML via Section Rendering API
        const searchUrl = window.routes?.search_url || '/search';
        const url = `${searchUrl}?section_id=${sectionId}&type=product&q=${encodeURIComponent(query)}`;

        const response = await fetch(url);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const html = await response.text();

        // Parse the response and extract the list items
        const tmpl = document.createElement('template');
        tmpl.innerHTML = html;

        const items = tmpl.content.querySelectorAll('li.slider__item');

        if (items.length === 0) return;

        // Inject into the grid
        const grid = this.querySelector(`#hr-alternatives-grid-${this.dataset.gridId}`);
        if (!grid) {
          console.warn('HelloRetailAlternatives: Grid element not found');
          return;
        }

        items.forEach((item) => grid.appendChild(item));

        // Attach click tracking to product links and cart buttons.
        //
        // Two challenges:
        // 1) Product link clicks navigate the page — hrq.push may not fire
        //    in time. We use navigator.sendBeacon as a reliable fire-and-forget.
        // 2) Shopify's quick-add.js calls stopImmediatePropagation(), blocking
        //    delegated listeners. Setting data-hr-tracking-key on each
        //    <product-card> lets the native initHrTracking() in main.js
        //    attach its own listeners during connectedCallback.
        if (trackingKey) {
          // Product links: use sendBeacon to fire before page unloads
          grid.querySelectorAll('a.js-prod-link, a.card-link').forEach((link) => {
            link.addEventListener('click', () => {
              try {
                const payload = JSON.stringify({
                  trackingKey: trackingKey,
                  websiteUuid: window.HelloRetailService?.websiteUuid || ''
                });
                if (navigator.sendBeacon) {
                  navigator.sendBeacon(
                    'https://core.helloretail.com/serve/collect/click',
                    new Blob([payload], { type: 'application/json' })
                  );
                }
              } catch (e) { /* best effort */ }
              // Also push to hrq as fallback
              (window.hrq = window.hrq || []).push(['trackClick', trackingKey]);
            });
          });

          // Cart buttons: set data-hr-tracking-key on each <product-card>
          // so initHrTracking() in main.js attaches its own click listeners
          grid.querySelectorAll('product-card').forEach((card) => {
            card.dataset.hrTrackingKey = trackingKey;
            // If the card was already initialized, manually attach tracking
            if (typeof card.initHrTracking === 'function') {
              card.initHrTracking();
            }
          });
        }

        // Show the section
        this.style.display = '';

        // Re-initialize the carousel-slider if present
        const carouselSlider = this.querySelector('carousel-slider');
        if (carouselSlider && typeof carouselSlider.reset === 'function') {
          carouselSlider.reset();
        }

      } catch (error) {
        console.error('HelloRetailAlternatives: Error:', error);
      }
    }

    waitForService() {
      return new Promise((resolve) => {
        let retries = 0;
        const interval = setInterval(() => {
          retries++;
          if (window.HelloRetailService) {
            clearInterval(interval);
            resolve(true);
          } else if (retries > 20) {
            clearInterval(interval);
            resolve(false);
          }
        }, 250);
      });
    }
  }

  customElements.define('hello-retail-alternatives', HelloRetailAlternatives);
})();
